import { useMemo } from 'react';
import { capitalize, orderBy, toString } from 'lodash';

import { REGEXPS } from '@/common';
import { DEPENDENT_RELATIONSHIP_ME } from '@/modules/employee/employee.types';

import type { PersonSpendLimit } from '../queries/useGetPersonSpendLimits.query';
import type { DependentSpendLimit } from '../types';

interface UseSpendLimitsDataProps {
  personSpendLimits: PersonSpendLimit[];
  employeeId?: string;
  employeeName?: string;
}

const DEFAULT_EMPLOYEE_NAME = 'Employee';
const DEFAULT_UNKNOWN_NAME = 'Unknown';
const SORT_BY_PARAM_NAME = 'name';

const formatRelationshipType = (relationshipType?: string): string => {
  if (!relationshipType) return '';
  return capitalize(relationshipType).replace(REGEXPS.DASH_SYMBOL, ' ');
};

export const useSpendLimitsData = ({
  personSpendLimits = [],
  employeeId,
  employeeName,
}: UseSpendLimitsDataProps): DependentSpendLimit[] => {
  return useMemo(() => {
    if (!personSpendLimits.length) return [];

    const result: DependentSpendLimit[] = personSpendLimits.map((limit) => {
      if (limit.dependentId === null) {
        return {
          id: employeeId || toString(limit.employeeId),
          name: employeeName || DEFAULT_EMPLOYEE_NAME,
          relationshipLabel: DEPENDENT_RELATIONSHIP_ME,
          isEmployee: true,
          personSpendLimit: limit.personSpendLimit,
          totalSpent: limit.totalSpent,
        };
      }

      const dependentId = toString(limit.dependentId);
      const fullName = `${limit.dependent?.firstName || ''} ${limit.dependent?.lastName || ''}`.trim();

      return {
        id: dependentId,
        name: fullName || DEFAULT_UNKNOWN_NAME,
        relationshipLabel: formatRelationshipType(limit.dependent?.relationshipType),
        isEmployee: false,
        personSpendLimit: limit.personSpendLimit,
        totalSpent: limit.totalSpent,
      };
    });

    return orderBy(result, [(item) => !item.isEmployee, SORT_BY_PARAM_NAME], ['asc', 'asc']);
  }, [personSpendLimits, employeeId, employeeName]);
};
